import {
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  InputAdornment,
  TextField,
} from "@mui/material";
import { CalendarIcon, DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { ReactElement, ReactNode, useState } from "react";

import { SecondaryDropdown } from "../SecondaryDropdown";
import { SecondaryTextField } from "../SecondaryTextField";
import Select from "./FormSelect";

function formatDateForDisplay(dateTime: DateTime): string {
  return dateTime.toLocaleString(DateTime.DATE_MED);
}

export type DateRange = {
  startDate: DateTime;
  endDate: DateTime;
};

export type DateRangePickerShortCut = {
  title: string;
  value: DateRange;
};

export type DateRangePickerProps = {
  value: DateRange | null;
  shortcuts?: DateRangePickerShortCut[];
  onChange: (newValue: DateRange) => void;
  label?: ReactNode;
  fullWidth?: boolean;
  secondary?: boolean;
  minDate?: DateTime;
  maxDate?: DateTime;
  error?: boolean;
  helperText?: string;
};

export default function DateRangePicker({
  value,
  shortcuts,
  onChange,
  label,
  fullWidth,
  secondary,
  minDate,
  maxDate,
  error,
  helperText,
}: DateRangePickerProps): ReactElement {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<DateTime | null>(
    value?.startDate || null
  );
  const [customEndDate, setCustomEndDate] = useState<DateTime | null>(
    value?.endDate || null
  );

  function handleDropdownSelect(newValue: DateRange) {
    setDialogOpen(false);
    onChange(newValue);
    setCustomStartDate(newValue.startDate);
    setCustomEndDate(newValue.endDate);
  }

  function handleDropdownSelectCustom() {
    setDialogOpen(true);
  }

  const dialog = (
    <Dialog
      title="Select A Date Range"
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
    >
      <DialogTitle>Select a Date Range</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <DatePicker
            value={customStartDate}
            onChange={val => setCustomStartDate(val)}
            minDate={minDate}
            maxDate={customEndDate ?? maxDate}
          />
          <Box sx={{ margin: 2 }}>-</Box>
          <DatePicker
            value={customEndDate}
            onChange={val => setCustomEndDate(val)}
            minDate={customStartDate || minDate}
            maxDate={maxDate}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!customStartDate || !customEndDate}
          onClick={() => {
            handleDropdownSelect({
              startDate: customStartDate!,
              endDate: customEndDate!,
            });
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  const valueText = value
    ? `${formatDateForDisplay(value.startDate)} - ${formatDateForDisplay(value.endDate)}`
    : "";

  const endAdornment = (
    <InputAdornment position="end">
      <CalendarIcon sx={{ height: "20px" }} />
    </InputAdornment>
  );

  if (shortcuts?.length) {
    const Component = secondary ? SecondaryDropdown : Select;
    return (
      <>
        <Component
          label={label}
          native={false}
          displayEmpty={true}
          renderValue={() => valueText}
          sx={{ minWidth: "200px" }}
          fullWidth={fullWidth}
          InputProps={{ endAdornment }}
          error={error}
          helperText={helperText}
        >
          {shortcuts.map(shortcut => (
            <MenuItem
              key={shortcut.title}
              onClick={() => handleDropdownSelect(shortcut.value)}
            >
              {shortcut.title}
            </MenuItem>
          ))}
          <MenuItem onClick={handleDropdownSelectCustom}>
            Select A Date Range...
          </MenuItem>
        </Component>
        {dialog}
      </>
    );
  }

  const Component = secondary ? SecondaryTextField : TextField;
  return (
    <>
      <Component
        label={label}
        value={valueText}
        onClick={handleDropdownSelectCustom}
        sx={{ minWidth: "200px" }}
        fullWidth={fullWidth}
        slotProps={{ input: { endAdornment } }}
        InputLabelProps={{ shrink: true }}
        error={error}
        helperText={helperText}
      />
      {dialog}
    </>
  );
}
